/**
 * Super Admin — Platform: Health / Costs / Errors / Notifications.
 */
import { useEffect, useState, useCallback } from "react";
import { Activity, Banknote, AlertCircle, BellRing } from "lucide-react";
import { apiClient } from "../../services/api";
import { SectionHeader } from "./SuperadminApp";
import { Card, TableShell, Th, Td, EmptyState, fmtINR, relTime, FilterPills, SummaryTiles, StatusBadge, card } from "./_atoms";

// ───────────────────────────────────────────────────────────────────
export function PlatformHealth() {
  const [services, setServices] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/superadmin/platform/health").then((r) => setServices(r.data?.services || []));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30_000); return () => clearInterval(id); }, [load]);
  return (
    <div className="space-y-4" data-testid="platform-health">
      <SectionHeader title="API Health" onRefresh={load} />
      {services.length === 0 ? <EmptyState title="No checks recorded" sub="Health checks will populate as the worker writes status entries." /> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {services.map((s) => {
            const ok = s.status === "ok" || s.status === "healthy";
            return (
              <Card key={s.service} data-testid={`svc-${s.service}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Activity size={16} style={{ color: ok ? "var(--success, #22C55E)" : "var(--error, #EF4444)" }} />
                  <strong style={{ textTransform: "uppercase", fontSize: 13 }}>{s.service}</strong>
                </div>
                <div style={{ fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900, marginTop: 4, color: ok ? "var(--success, #22C55E)" : "var(--error, #EF4444)" }}>
                  {(s.status || "unknown").toUpperCase()}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
                  {s.response_ms ? `${s.response_ms}ms · ` : ""}{s.checked_at ? relTime(s.checked_at) : "never"}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function PlatformCosts() {
  const [data, setData] = useState({ services: {}, thresholds: {} });
  const load = useCallback(() => {
    apiClient.get("/superadmin/platform/costs").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const svcs = Object.keys(data.services || {});
  return (
    <div className="space-y-4" data-testid="platform-costs">
      <SectionHeader title="Cost Monitor" onRefresh={load} />
      {svcs.length === 0 ? <EmptyState title="No usage logged" sub="API usage rolls in once the apps log calls to the api_usage collection." /> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {svcs.map((k) => {
            const s = data.services[k];
            const monthThreshold = data.thresholds?.[`${k}_monthly`];
            const utilization = monthThreshold ? (s.month_cost / monthThreshold) : 0;
            const warn = utilization > 0.8;
            return (
              <Card key={k} data-testid={`cost-${k}`} style={warn ? { borderLeft: "3px solid var(--warning, #F59E0B)" } : {}}>
                <strong style={{ textTransform: "uppercase", fontSize: 12 }}>{k}</strong>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  <div>Today: <strong>{fmtINR(s.today_cost)}</strong> ({s.today_calls} calls)</div>
                  <div style={{ marginTop: 4 }}>Month: <strong>{fmtINR(s.month_cost)}</strong> ({s.month_calls} calls)</div>
                  {monthThreshold ? (
                    <div style={{ fontSize: 11, color: warn ? "var(--warning, #F59E0B)" : "var(--text-tertiary, #8A8884)", marginTop: 4 }}>
                      {Math.round(utilization * 100)}% of {fmtINR(monthThreshold)} budget
                    </div>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function PlatformErrors() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/superadmin/platform/error-feed", { params: { limit: 100 } })
      .then((r) => setRows(r.data?.errors || []));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="platform-errors">
      <SectionHeader title="Error Logs" onRefresh={load} />
      {rows.length === 0 ? <EmptyState title="No recent errors" sub="Last 24h is clean." /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Type</Th><Th>Reference</Th><Th>User</Th><Th>Detail</Th><Th>When</Th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.ref_id || r.error_id || i}>
                  <Td><StatusBadge value={r.type || "error"} /></Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace" }}>{(r.ref_id || r.booking_id || r.error_id || "").slice(0, 14)}</Td>
                  <Td style={{ fontSize: 12 }}>{(r.user_id || "").slice(0, 12) || "—"}</Td>
                  <Td style={{ fontSize: 12 }}>{r.error_reason || r.message || r.error || "—"}</Td>
                  <Td>{relTime(r.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function NotificationLog() {
  const [data, setData] = useState({ notifications: [], total: 0, read_count: 0 });
  const [type, setType] = useState("");
  const load = useCallback(() => {
    apiClient.get("/superadmin/notifications/log", { params: { type: type || undefined, limit: 100 } })
      .then((r) => setData(r.data || { notifications: [] }));
  }, [type]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="notification-log">
      <SectionHeader title="Notification Log" onRefresh={load} />
      <SummaryTiles tiles={[
        { label: "Total", value: data.total },
        { label: "Read", value: data.read_count },
        { label: "Unread", value: Math.max(0, (data.total || 0) - (data.read_count || 0)) },
        { label: "Showing", value: data.notifications.length },
      ]} />
      <Card>
        <FilterPills
          value={type} onChange={setType}
          options={[
            { value: "", label: "All" },
            { value: "broadcast", label: "Broadcast" },
            { value: "booking", label: "Booking" },
            { value: "marketplace_message", label: "Marketplace" },
            { value: "ai_coach", label: "AI Coach" },
            { value: "professional", label: "Professional" },
          ]}
          testid="notif-type-filter"
        />
      </Card>
      {data.notifications.length === 0 ? <EmptyState title="No notifications" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Title</Th><Th>Type</Th><Th>User</Th><Th>Read</Th><Th>Sent</Th></tr></thead>
            <tbody>
              {data.notifications.map((n) => (
                <tr key={n.notification_id}>
                  <Td><strong>{n.title}</strong><div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{n.message?.slice(0, 80) || ""}</div></Td>
                  <Td><StatusBadge value={n.type} /></Td>
                  <Td style={{ fontSize: 12 }}>{(n.user_id || "").slice(0, 12)}</Td>
                  <Td>{n.read ? "✓" : "—"}</Td>
                  <Td>{relTime(n.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

// ── Session 4C — Scanner Diagnostics (Label Intelligence) ────────────
export function ScannerDiagnostics() {
  const [d, setD] = useState(null);
  useEffect(() => {
    apiClient.get("/superadmin/label-diagnostics").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  if (!d) return <div className="p-4 text-sm" data-testid="scanner-diagnostics">Loading…</div>;
  const rate = d.understanding_rate_pct;
  return (
    <div className="space-y-4" data-testid="scanner-diagnostics">
      {/* THE NUMBER THAT MATTERS — optimise this, not scan volume */}
      <div style={card} data-testid="understanding-rate-card">
        <div style={{ fontSize: 12, opacity: 0.65 }}>Successful understanding rate</div>
        <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.1 }}>
          {rate != null ? `${rate}%` : "—"}
        </div>
        <div style={{ fontSize: 12, opacity: 0.65 }}>
          {d.displayed ?? 0} displayed results (clean/flagged, confidence &gt; 0.60) of {d.attempts ?? 0} label scan attempts
        </div>
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Validation flags by rule</div>
        {Object.keys(d.validation_flag_frequency || {}).length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>No flags recorded yet.</div>
        ) : (
          Object.entries(d.validation_flag_frequency).map(([rule, n]) => (
            <div key={rule} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
              <span>{rule}</span><b>{n}</b>
            </div>
          ))
        )}
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Capture quality &amp; wrong-tab</div>
        {[
          ["Quality rejects (client, free)", d.quality_reject_count],
          ["Quality reject rate", d.quality_reject_rate_pct != null ? `${d.quality_reject_rate_pct}%` : "—"],
          ["Retakes after reject", d.retake_after_reject_count],
          ["Wrong-mode fired · Packet tab", d.wrong_mode_fired?.packet_tab],
          ["Wrong-mode fired · Scan Food tab", d.wrong_mode_fired?.scan_food_tab],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
            <span>{k}</span><b>{v ?? 0}</b>
          </div>
        ))}
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Result-screen engagement</div>
        {Object.entries(d.ui_engagement || {}).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
            <span>{k.replace(/_/g, " ")}</span><b>{v}</b>
          </div>
        ))}
      </div>
    </div>
  );
}
